<template>
  <div>
    <FormField name="employer">
      <FormItem class="flex flex-col space-y-2">
        <FormLabel class="flex items-center">
          <Building2 class="w-4 h-4 mr-2 items-center" />
          Employer
          <span class="text-red-500 ml-0.5">*</span>
        </FormLabel>
        <div>
          <div class="relative" ref="companyDropdownRef">
            <Button 
              type="button"
              variant="outline" 
              class="w-full justify-between h-10 px-3 py-2 text-sm"
              @click="handleCompanyInputFocus"
            >
              <div
class="flex items-center gap-2 font-normal"
                :class="{ 'text-gray-500': !selectedEmployerDisplay?.name }"
              >
                <img
                  v-if="selectedEmployerDisplay?.logo"
                  :src="selectedEmployerDisplay.logo"
                  :alt="selectedEmployerDisplay.name"
                  class="w-5 h-5 object-contain"
                />
                {{ selectedEmployerDisplay?.name || 'Select employer' }}
              </div>
              <ChevronDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>

            <div 
              v-if="isDropdownOpen" 
              class="absolute text-sm top-full mt-1 w-96 min-w-[var(--radix-combobox-trigger-width)] rounded-md border bg-popover text-popover-foreground shadow-md outline-none z-50"
            >
              <div class="sticky top-0 bg-popover border-b border-border z-10">
                <div class="relative w-full">
                  <input
                    ref="searchInput"
                    class="w-full pl-9 focus-visible:ring-0 border-0 rounded-none h-10 bg-transparent outline-none placeholder:text-muted-foreground"
                    placeholder="Search employer..."
                    v-model="searchTerm"
                    @input="handleEmployerSearch"
                    @keydown="handleKeyDown"
                  />
                  <span class="absolute start-0 inset-y-0 flex items-center justify-center px-3">
                    <Search class="size-4 text-muted-foreground" />
                  </span>
                </div>
              </div>

              <div 
                ref="employerListContainer"
                class="max-h-[300px] overflow-y-auto"
                @scroll="handleScroll"
              >
                <div v-if="employerStore.employerOptions.length === 0 && !employerStore.isLoading" class="p-2 text-sm text-center text-muted-foreground">
                  No employers found.
                </div>

                <div class="w-full">
                  <div
                    v-for="(employer, index) in employerStore.employerOptions"
                    :key="employer.id"
                    class="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent hover:text-accent-foreground cursor-pointer"
                    :class="{ 
                      'bg-accent': employer.id === selectedEmployerId || index === highlightedIndex,
                      'text-accent-foreground': index === highlightedIndex
                    }"
                    @click="selectEmployer(employer)"
                  >
                    <div class="flex items-center justify-between w-full">
                      <div class="flex items-center gap-2">
                        <img
                          v-if="employer.logo"
                          :src="employer.logo"
                          :alt="employer.name"
                          class="w-5 h-5 object-contain"
                        />
                        {{ employer.name }}
                      </div>
                      <Check v-if="employer.id === selectedEmployerId" class="h-4 w-4" />
                    </div>
                  </div>
                  <div
                    v-if="employerStore.isLoading"
                    class="p-2 text-sm text-center text-muted-foreground"
                  >
                    Loading...
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FormDescription class="mt-2">
            Can't find the employer?
            <button
              type="button"
              @click="toggleCreateEmployer"
              class="inline-flex text-cosmicPurple p-0 items-center"
            >
              Create new employer
              <ChevronDown
                class="w-4 h-4 ml-1"
                :class="{ 'rotate-180': showCreateEmployer }"
              />
            </button>
          </FormDescription>
        </div>
      </FormItem>
    </FormField>

    <!-- Create New Employer (Conditional) -->
    <div v-if="showCreateEmployer" class="bg-lime-100 rounded-md p-4 mt-2 fadeIn">
      <FormField name="newEmployer">
        <FormItem class="flex flex-col gap-2">
          <FormLabel>New Employer Name</FormLabel>
          <FormControl>
            <Input
              v-model="newEmployerName"
              placeholder="Enter new employer name"
              type="text"
              class="bg-white"
            />
          </FormControl>
          <Button
            type="button"
            @click="createNewEmployer"
            class="mt-2 text-xs ml-auto"
            variant="default"
          >
            Add New Employer
          </Button>
        </FormItem>
      </FormField>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useEmployerStore } from '@/stores/employer'
import type { Employer } from '@/types/employer'

import {
  FormField,
  FormControl,
  FormDescription,
  FormItem,
  FormLabel
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ChevronDown, Search, Check, Building2 } from 'lucide-vue-next'
import { onClickOutside } from '@vueuse/core'

const props = defineProps<{
  modelValue: number | null
  initialEmployer?: { id: number; name: string; logo?: string | null } | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null): void
  (e: 'error', message: string): void
}>()

// Store
const employerStore = useEmployerStore()

// UI state
const showCreateEmployer = ref(false)
const newEmployerName = ref('')
const employerListContainer = ref<HTMLElement | null>(null)
const searchTerm = ref('')
const isDropdownOpen = ref(false)
const companyDropdownRef = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const highlightedIndex = ref(-1)

const selectedEmployerId = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Computed properties
const selectedEmployer = computed((): Employer | null => {
  return employerStore.employerOptions.find(
    (employer: Employer) => employer.id === selectedEmployerId.value
  ) || null
})

const selectedEmployerDisplay = computed(() => {
  if (selectedEmployer.value) return selectedEmployer.value
  if (props.initialEmployer && props.initialEmployer.id === selectedEmployerId.value) {
    return props.initialEmployer
  }
  return null
})

const selectEmployer = (employer: Employer) => {
  selectedEmployerId.value = employer.id
  isDropdownOpen.value = false
  searchTerm.value = ''
  highlightedIndex.value = -1
}

const handleCompanyInputFocus = () => {
  isDropdownOpen.value = !isDropdownOpen.value
  if (isDropdownOpen.value) {
    searchTerm.value = ''
    highlightedIndex.value = -1
    // Focus the search input after the DOM updates
    setTimeout(() => {
      searchInput.value?.focus()
    }, 0)
  }
}

const handleKeyDown = (event: KeyboardEvent) => {
  const options = employerStore.employerOptions
  
  switch (event.key) {
    case 'Tab':
      // Close dropdown on Tab
      isDropdownOpen.value = false
      highlightedIndex.value = -1
      break
      
    case 'ArrowDown':
      event.preventDefault()
      if (options.length > 0) {
        highlightedIndex.value = Math.min(highlightedIndex.value + 1, options.length - 1)
      }
      break
      
    case 'ArrowUp':
      event.preventDefault()
      if (options.length > 0) {
        highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
      }
      break
      
    case 'Enter':
      event.preventDefault()
      if (highlightedIndex.value >= 0 && highlightedIndex.value < options.length) {
        selectEmployer(options[highlightedIndex.value])
      }
      break
      
    case 'Escape':
      isDropdownOpen.value = false
      highlightedIndex.value = -1
      break
  }
}

const toggleCreateEmployer = () => {
  showCreateEmployer.value = !showCreateEmployer.value
  if (!showCreateEmployer.value) {
    newEmployerName.value = ''
  }
}

const createNewEmployer = async () => {
  try {
    const result = await employerStore.checkAndCreateEmployer(newEmployerName.value)
    employerStore.ensureEmployerInOptions(result)
    selectedEmployerId.value = result.id
    showCreateEmployer.value = false
    newEmployerName.value = ''
  } catch (error: any) {
    const message = (error as Error).message || 'Failed to create employer'
    emit('error', message)
  }
}

// Debounced search function
let searchTimeout: ReturnType<typeof setTimeout>
const handleEmployerSearch = () => {
  highlightedIndex.value = -1 // Reset highlight when searching
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(async () => {
    if (searchTerm.value.length >= 2) {
      await employerStore.fetchEmployerOptions(searchTerm.value)
    }
  }, 300)
}

// Infinite scroll handler
const handleScroll = async (event: Event) => {
  const container = event.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = container
  
  // Check if we're near the bottom (within 50px) and not currently loading
  if (scrollHeight - (scrollTop + clientHeight) < 50 && !employerStore.isLoading && employerStore.nextPage) {
    try {
      await employerStore.loadMoreEmployers()
    } catch (error) {
      console.error('Error loading more employers:', error)
    }
  }
}

// When the selected employer is set externally (e.g. URL autofill) and the
// employer isn't in the currently-loaded list, pin it so the display always works.
watch(
  () => props.modelValue,
  (newId) => {
    if (!newId) return
    const hint = props.initialEmployer
    if (hint && hint.id === newId) {
      employerStore.ensureEmployerInOptions(hint)
    }
  }
)

// Initialize employer options if needed
onMounted(async () => {
  // Only fetch if no options have been loaded yet — avoids overwriting a list
  // that was already populated (e.g. by the URL-autofill flow in JobForm).
  if (employerStore.employerOptions.length === 0) {
    await employerStore.fetchEmployerOptions()
  }

  // After fetch, ensure any pre-selected employer is still present.
  if (props.modelValue && props.initialEmployer?.id === props.modelValue) {
    employerStore.ensureEmployerInOptions(props.initialEmployer)
  }

  if (companyDropdownRef.value) {
    onClickOutside(companyDropdownRef, () => {
      isDropdownOpen.value = false
    })
  }
})
</script>